import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import Logo from '../Assets/Home/MARVEL.png'

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Products', path: '/products' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Blog', path: '/blog' },
    ];

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        setIsOpen(false);
    };

    return (
        /* - top-2 and py-2 for the shorter mobile height.
           - bg-white/80 for reduced transparency.
        */
        <header className="fixed top-2 md:top-4 left-1/2 transform -translate-x-1/2 w-[95%] max-w-7xl bg-white/80 backdrop-blur-md border border-slate-200 rounded-2xl shadow-lg z-50 px-4 md:px-6 py-2 md:py-3 transition-all duration-300">
            <div className="flex justify-between items-center">
                
                {/* Logo & Brand Section */}
                <Link to="/" onClick={scrollToTop} className="flex items-center space-x-2 group">
                    {/* Logo Image: h-7 on mobile, h-9 on desktop */}
                    <img 
                        src={Logo} 
                        alt="Marvel Sato Logo" 
                        className="h-7 w-auto md:h-9 object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                    
                    {/* Heading: text-sm on mobile (smaller as requested), text-xl/2xl on desktop */}
                    <h1 className="text-sm md:text-xl lg:text-2xl font-black text-slate-900 tracking-tighter italic whitespace-nowrap">
                        MARVEL <span className="text-orange-500 not-italic">SATO</span>
                    </h1>
                </Link>

                {/* Desktop Navigation */}
                <nav className="hidden md:block">
                    <ul className="flex items-center space-x-1">
                        {navLinks.map((link) => (
                            <li key={link.name} className="relative">
                                {link.name === 'Home' ? (
                                    <Link 
                                        to="/" 
                                        onClick={scrollToTop}
                                        className="relative z-10 text-sm font-bold text-slate-700 px-4 py-2 block transition-colors duration-300 hover:text-orange-500"
                                    >
                                        {link.name}
                                    </Link>
                                ) : (
                                    <HashLink 
                                        smooth 
                                        to={link.path} 
                                        className="relative z-10 text-sm font-bold text-slate-700 px-4 py-2 block transition-colors duration-300 hover:text-orange-500"
                                    >
                                        {link.name}
                                    </HashLink>
                                )}
                            </li>
                        ))}
                        <li className="pl-4">
                            <Link 
                                to="/contact" 
                                className="bg-orange-500 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-slate-900 transition-all duration-300 shadow-md shadow-orange-200 block text-center"
                            >
                                Contact Us
                            </Link>
                        </li>
                    </ul>
                </nav>

                {/* Mobile Menu Button */}
                <button 
                    className="md:hidden p-1.5 text-slate-700 focus:outline-none"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {/* Mobile Navigation Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden overflow-hidden"
                    >
                        <ul className="flex flex-col space-y-1 pt-3 pb-2 border-t border-slate-100 mt-2">
                            {navLinks.map((link, i) => (
                                <motion.li 
                                    key={link.name}
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.05 }}
                                >
                                    <HashLink 
                                        smooth
                                        to={link.path} 
                                        className="text-slate-700 text-sm font-bold py-2.5 px-4 block hover:bg-orange-50 rounded-xl transition-colors"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {link.name}
                                    </HashLink>
                                </motion.li>
                            ))}
                            <motion.li
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: navLinks.length * 0.05 }}
                            >
                                <HashLink 
                                    smooth
                                    to="/contact" 
                                    className="bg-orange-500 text-white text-center text-sm font-bold py-2.5 px-4 block rounded-xl mt-1"
                                    onClick={() => setIsOpen(false)}
                                >
                                    Contact Us
                                </HashLink>
                            </motion.li>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;